import { AdControlResponseMap, AdType } from './factory';

// 기본 모델 인터페이스
interface BaseAdControlModelProps<T extends AdType> {
  response: AdControlResponseMap[T];
  unitId?: string;
}

// Banner 전용 모델
export class AdControlBannerModel {
  constructor(private _props: BaseAdControlModelProps<'banner'>) {}

  get isSuccess() {
    return this._props.response.code === 0 && this._props.response.result !== null;
  }

  get errorCode() {
    return this._props.response.code;
  }

  get errorMessage() {
    return this._props.response.msg;
  }

  get unitId() {
    return this._props.unitId;
  }

  get isVisible() {
    return this.isSuccess && this.hasAdContent;
  }

  private get hasAdContent() {
    if (!this._props.response.result) return false;
    const result = this._props.response.result;
    return !!result?.ad;
  }

  get bannerHtml() {
    if (!this._props.response.result) return '';
    const result = this._props.response.result;
    return result?.ad || '';
  }

  get bannerWidth() {
    if (!this._props.response.result) return 0;
    const result = this._props.response.result;
    return result?.w || 0;
  }

  get bannerHeight() {
    if (!this._props.response.result) return 0;
    const result = this._props.response.result;
    return result?.h || 0;
  }

  get format() {
    if (!this._props.response.result) return '';
    const result = this._props.response.result;
    return result?.format || '';
  }
}

// Native 전용 모델
export class AdControlNativeModel {
  constructor(private _props: BaseAdControlModelProps<'native'>) {}

  get isSuccess() {
    return this._props.response.code === 0 && this._props.response.result !== null;
  }

  get errorCode() {
    return this._props.response.code;
  }

  get errorMessage() {
    return this._props.response.msg;
  }

  get unitId() {
    return this._props.unitId;
  }

  get isVisible() {
    return this.isSuccess && this.hasAdContent;
  }

  private get hasAdContent() {
    if (!this._props.response.result) return false;
    const result = this._props.response.result;
    return !!(result?.headline || result?.body);
  }

  get nativeData() {
    if (!this._props.response.result) return null;
    const result = this._props.response.result;

    if (!result) return null;

    return {
      advertiser: result.advertiser,
      headline: result.headline,
      body: result.body,
      callToAction: result.extra.button,
      destinationUrl: result.destinationURL,
      asset: result.asset,
      clickTracker: result.clickTracker,
      imprTracker: result.imprTracker,
      pixelTracker: result.pixelTracker,
      tag: result.extra.tag,
      extra_tag: result.extra.extra_tag,
    };
  }

  get nativeImageUrl() {
    const data = this.nativeData;
    return data?.asset || '';
  }

  // 트래킹 메서드들
  private _impressionTracked = false;
  private _clickTracked = false;

  async trackImpression() {
    const data = this.nativeData;
    if (!data?.imprTracker || this._impressionTracked) return;

    try {
      this._impressionTracked = true;
      await fetch(data.imprTracker, { method: 'GET' });
    } catch (error) {
      console.error('Failed to track impression:', error);
      this._impressionTracked = false;
    }
  }

  async trackClick() {
    const data = this.nativeData;
    if (!data?.clickTracker || this._clickTracked) return;

    try {
      this._clickTracked = true;
      await fetch(data.clickTracker, { method: 'GET' });
    } catch (error) {
      console.error('Failed to track click:', error);
      this._clickTracked = false;
    }
  }

  async handleNativeClick() {
    await this.trackClick();
    const data = this.nativeData;
    if (data?.destinationUrl && typeof window !== 'undefined') {
      window.open(data.destinationUrl, '_blank');
    }
  }

  getDestinationUrl() {
    const data = this.nativeData;
    return data?.destinationUrl || '';
  }
}
